import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Check, ChevronsUpDown } from "lucide-react"
import { z } from "zod"
import { cn } from "@/lib/utils"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { ButtonVariant } from "@/constants/button/button"
import { AppDispatch } from "@/redux"
import { useDispatch } from "react-redux"
import { OptionProps } from "@/types/workflow"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { useEffect, useState } from "react"
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname"

const formSchema = z.object({
  optionValue: z.string(),
})

export const DropdownOption: React.FC<OptionProps> = ({
  stepId,
  code,
  order,
  selectOptions,
  selectedValue,
  name,
  description
}: OptionProps) => {
  const dispatch: AppDispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      optionValue: ''
    },
  })
  const optionValue = form.watch('optionValue')

  useEffect(() => {
    if (selectedValue) {
      form.setValue('optionValue', selectedValue)
    }
  }, [selectedValue, form])

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          className="w-auto font-semibold"
          variant={ButtonVariant.DEFAULT}
        >
          Update
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="mb-6">
            <div className="flex flex-row items-start justify-between">
              <div className="flex flex-col items-start">
                <h4 className="text-primary font-semibold text-xl leading-5 mb-2">
                  Option Setup: {name}
                </h4>
                <p className="text-secondary text-sm mb-2 text-left font-normal">{description}</p>
              </div>
              <div className="flex items-center">
                <DialogClose asChild>
                  <Button variant={ButtonVariant.OUTLINE} className="mx-4">Cancel</Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button
                    type="submit"
                    disabled={optionValue?.length === 0}
                    onClick={() => {
                      if (optionValue?.length > 0) {
                        dispatch(workflowActions.setStepOption({
                          stepId,
                          order,
                          option: code,
                          optionValue
                        }))
                      }
                    }}
                  >
                    Update Option
                  </Button>
                </DialogClose>
              </div>
            </div>
          </DialogTitle>
          <div className="">
            <div className="my-4" />
            <Form {...form}>
              <form className="space-y-6">
                <FormField
                  control={form.control}
                  name="optionValue"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{name}</FormLabel>
                      <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              role="combobox"
                              className={cn(
                                INPUT_FIELD_CLASSNAME,
                                "w-[300px] justify-between bg-foreground text-primary",
                                !field.value && "text-muted"
                              )}
                            >
                              {field.value
                                ? selectOptions.find(
                                  (option) => option.id === field.value
                                )?.value
                                : "Select Option"}
                              <ChevronsUpDown className="opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0">
                          <Command>
                            <CommandInput
                              placeholder="Search option..."
                              className="h-9"
                            />
                            <CommandList onWheel={e => e.stopPropagation()}>
                              <CommandEmpty>No options found.</CommandEmpty>
                              {selectOptions.map((option) => (
                                <CommandItem
                                  value={option.value}
                                  key={option.id}
                                  onSelect={() => {
                                    form.setValue("optionValue", option.id)
                                    setOpen(false)
                                  }}
                                >
                                  {option.value}
                                  <Check
                                    className={cn(
                                      "ml-auto",
                                      option.value === field.value
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}
